import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { buildAllocationUsers } from "./resource-allocation-builder";
import type {
  AllocationDealRow,
  AllocationTicketRow,
  AllocationTimeEntryRow,
  AllocationUser,
  AllocationUserRow,
  AllocationUserSkillRow,
} from "./resource-allocation-types";

export type { AllocationUser } from "./resource-allocation-types";

interface UseResourceAllocationOptions {
  enabled?: boolean;
}

export function useResourceAllocation(options: UseResourceAllocationOptions = {}) {
  const { isAuthenticated } = useUniversalAuth();
  const { enabled = true } = options;

  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ["resource-allocation"],
    queryFn: async () => {
      const supabase = getBrowserSupabaseClient();

      const [usersRes, dealsRes, ticketsRes, timeEntriesRes, userSkillsRes] = await Promise.all([
        supabase
          .from("app_users")
          .select("id, first_name, last_name, email, available_hours, is_billable")
          .eq("is_active", true),
        supabase
          .from("deals")
          .select("id, title, estimated_hours, assigned_users, engineer_ids, stage"),
        supabase
          .from("tickets")
          .select("id, title, estimated_hours, actual_hours, assigned_to, status"),
        supabase
          .from("time_entries")
          .select("user_id, hours, date_worked"),
        supabase
          .from("app_user_skills")
          .select("user_id, skill_id, grade, skill:skills_catalog(name)"),
      ]);

      if (usersRes.error) throw usersRes.error;
      if (dealsRes.error) throw dealsRes.error;
      if (ticketsRes.error) throw ticketsRes.error;
      if (timeEntriesRes.error) throw timeEntriesRes.error;
      if (userSkillsRes.error) throw userSkillsRes.error;

      return {
        users: (usersRes.data || []),
        deals: (dealsRes.data || []),
        tickets: (ticketsRes.data || []),
        timeEntries: (timeEntriesRes.data || []),
        userSkills: (userSkillsRes.data || []),
      };
    },
    enabled: isAuthenticated && enabled,
    staleTime: 2 * 60 * 1000,
  });

  const allocationUsers = useMemo((): AllocationUser[] => buildAllocationUsers(rawData), [rawData]);

  return {
    allocationUsers,
    isLoading,
    error: error ? new Error((error).message) : null,
  };
}
